/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useExportEligibilityData
 */
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import api from 'client/index';
import AppEvents, { Events, Messages } from '@veritable/common/utils/appEvents';
import { downloadBlobFile } from '@veritable/common/utils';
import { MONTH_DATE_YEAR_TIME_FORMAT } from '@veritable/common/constants';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { getStatus } from 'utils/eligibility/eligibilityResponse';
import { dataKeys } from 'utils/eligibility/eligibilityRequests';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { ELIGIBILITY_ADF_FIELD_MAP } from 'utils/adfUtils';

/**
 * Mutate to export eligibility data
 */

interface IProps {
	searchParam: EligibilityRequests.IApiParams;
}

const useExportEligibilityData = (): UseMutationResult<unknown, IAxiosError, IProps, unknown> => {
	return useMutation({
		mutationFn: async ({ searchParam }) => {
			const criteria = parseQueryFilterParams({
				query: searchParam?.criteria,
				field: dataKeys.coverageStatus,
				parser: getStatus,
			});
			const { data } = await api.EzapiApi.exportEligibilityRequest(
				{
					eligibilityRequestQueryModel: {
						...searchParam,
						...(buildCriteria(_.cloneDeep(criteria), ELIGIBILITY_ADF_FIELD_MAP) || {}),
					},
				},
				{ responseType: 'blob' },
			);

			downloadBlobFile(
				data,
				_.kebabCase(`eligibility-data-${dateParser().format(MONTH_DATE_YEAR_TIME_FORMAT)}`),
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'download',
			);
		},
		onSuccess: () => {
			AppEvents.publishApiSuccess('Download complete');
		},
		onMutate: () => {
			AppEvents.publish(Events.Loading, Messages.DownloadingFile);
		},
		onError: (error) => {
			const fileReader = new FileReader();
			fileReader.addEventListener('loadend', () => {
				const parsedError = { response: { data: JSON.parse(fileReader.result as string) } } as IAxiosError;
				AppEvents.publishApiError({ error: parsedError });
			});
			fileReader.readAsText(error.response?.data as unknown as AnyType);
		},
	});
};

export default useExportEligibilityData;
